import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Box, Container, IconButton } from "@mui/material";
import { ProgressBar } from "./components/ProgressBar";
import { StepDots } from "./components/StepDots";
import { ReactComponent as FayeIcon } from "../../icons/FayeBrand.svg";
import { motion, AnimatePresence } from "framer-motion";
import {
  useSignupSubmission,
  useSignupSubmissionByMember,
  useUpdateSession,
  useUpdateSessionForMember,
} from "../../services/signup";
import { ArrowLeft } from "lucide-react";
import useTitle from "../hooks/useTitle";
import { SignupState, StepRef, STEPS, ValidationError } from "./types";
import useIsMobile from "../hooks/useIsMobile";
import Collapse from "@mui/material/Collapse";
import { getLongestPathFromEnd, SignupStep } from "./flow";
import { buildSessionUpdate } from "./utils";
import { refreshToken } from "services/api";

const defaultFormData: SignupState = {};

export const SignupFlow = ({
  initialStep,
  initialFormData,
  setStepName,
  isDebug = false,
  onSignupComplete,
  signupFlow,
  onExit,
  useAuthenticatedUpdate = false,
  stepIndicator = "bar",
}: {
  initialStep?: STEPS;
  initialFormData?: SignupState;
  setStepName: (step: string) => void;
  isDebug?: boolean;
  onSignupComplete?: (completionStateName: string, isTerminus: boolean) => void;
  signupFlow: SignupStep[];
  onExit?: () => void;
  useAuthenticatedUpdate?: boolean;
  stepIndicator?: "bar" | "dots";
}) => {
  useTitle("Join Faye");
  const params = useParams();
  const stepRef = useRef<HTMLDivElement>(null);
  const [progressBarSx, setProgressBarSx] = useState({});
  const [formData, setFormData] = useState<SignupState>(
    initialFormData || defaultFormData,
  );
  const [currentStep, setCurrentStep] = useState(
    Math.max(
      signupFlow.findIndex((s) => s.name === initialStep),
      0,
    ),
  );
  const [, setIsMovingForward] = useState(true);
  // Using Map to preserve insertion order
  const stepRefs = useRef<Map<string, StepRef>>(new Map());
  const isMobile = useIsMobile();
  const initialStepDef =
    (initialStep && signupFlow.find((s) => s.name === initialStep)) ||
    signupFlow[0];
  const [showBack, setShowBack] = useState(
    !(initialStepDef?.backDisabled || false),
  );
  const { request: cookieUpdateRequest, loading: cookieUpdateLoading } =
    useUpdateSession();
  const {
    request: authenticatedUpdateRequest,
    loading: authenticatedUpdateLoading,
  } = useUpdateSessionForMember();
  const { request: cookieSubmissionRequest, loading: cookieSubmissionLoading } =
    useSignupSubmission();
  const {
    request: authenticatedSubmissionRequest,
    loading: authenticatedSubmissionLoading,
  } = useSignupSubmissionByMember();
  const updateRequest = useAuthenticatedUpdate
    ? authenticatedUpdateRequest
    : cookieUpdateRequest;
  const updateLoading = useAuthenticatedUpdate
    ? authenticatedUpdateLoading
    : cookieUpdateLoading;
  const submissionRequest = useAuthenticatedUpdate
    ? authenticatedSubmissionRequest
    : cookieSubmissionRequest;
  const submissionLoading = useAuthenticatedUpdate
    ? authenticatedSubmissionLoading
    : cookieSubmissionLoading;

  const handleNext = async (stepData?: Partial<SignupState>) => {
    const currentStepDef = signupFlow[currentStep];
    if (currentStepDef.isValid && !currentStepDef.isValid(stepData || {})) {
      // TODO(Kip): not sure how/if we handle in the step UX.
      throw new ValidationError("Invalid step");
    }
    const nextStepName = currentStepDef.nextStep?.(stepData || {});
    const updates = buildSessionUpdate(stepData, nextStepName);
    let resp = null;
    if (!isDebug) {
      resp = await updateRequest(updates);
      if (!resp) {
        // TODO(Kip): not sure how/if we handle in the step UX.
        throw new ValidationError("Error saving step");
      }
      if (currentStepDef.completionStateName) {
        const submissionResp = await submissionRequest({
          submissionState: currentStepDef.completionStateName,
        });
        if (!submissionResp) {
          throw new ValidationError("Error submitting signup");
        }
        if (currentStepDef.refreshTokenOnComplete) {
          await refreshToken();
        }
        onSignupComplete?.(
          currentStepDef.completionStateName,
          !!currentStepDef.signupTerminus,
        );
      }
    }
    // Not all steps have step data
    if (stepData) {
      setFormData((prev) => ({ ...prev, ...stepData }));
    }

    // Extract token from response if present
    let responseData = undefined;
    if (resp && typeof resp === "object" && "token" in resp) {
      responseData = { token: resp.token };
    }

    if (!nextStepName) return responseData;
    const nextStepInd = signupFlow.findIndex(
      (step) => step.name === nextStepName,
    );
    if (nextStepInd < 0) return responseData;
    setIsMovingForward(true);
    setCurrentStep(nextStepInd);
    const nextStepDef = signupFlow[nextStepInd];
    setShowBack(!nextStepDef.backDisabled);
    return responseData;
  };
  useEffect(() => {
    setStepName(steps[currentStep][0]);
  }, [currentStep]);

  useEffect(() => {
    if (params.step) {
      const step = signupFlow.findIndex((s) => s.name === params.step);
      setCurrentStep(step);
    }
  }, [params.step]);

  const handleBack = () => {
    setIsMovingForward(false);
    const currentStepDef = signupFlow[currentStep];
    const backStepName = !currentStepDef.backDisabled
      ? currentStepDef.defaultBackStep
      : null;
    if (!backStepName) {
      onExit?.();
      return;
    }
    const backStepInd = signupFlow.findIndex(
      (step) => step.name === backStepName,
    );
    const backStepDef = signupFlow[backStepInd];
    if (backStepInd < 0) return;
    setCurrentStep(backStepInd);
    setShowBack(!backStepDef.backDisabled);
  };

  const handleScroll = () => {
    const el = stepRef.current;
    if (el && el.scrollTop > 0) {
      setProgressBarSx({
        boxShadow: isMobile ? "0px 5px 8px 0px rgba(151, 93, 51, 0.07);" : "",
      });
    } else {
      setProgressBarSx({});
    }
  };
  const steps: [STEPS, React.ReactNode][] = signupFlow.map((step) => [
    step.name,
    <Box
      key={step.name}
      sx={{
        width: "100%",
      }}
    >
      {React.createElement(step.component, {
        key: step.name,
        ref: (ref: StepRef) => {
          if (ref) stepRefs.current.set(step.name, ref);
        },
        signupState: formData,
        onNext: handleNext,
        disabled: submissionLoading || updateLoading,
        isDebug,
      })}
    </Box>,
  ]);

  const totalStepsToEnd = getLongestPathFromEnd(signupFlow, undefined, true);
  const totalStepsToVeryEnd = getLongestPathFromEnd(
    signupFlow,
    undefined,
    false,
  );
  const stepsLeftToEnd = getLongestPathFromEnd(
    signupFlow,
    signupFlow[currentStep].name,
    true,
  );
  const stepsLeftToVeryEnd = getLongestPathFromEnd(
    signupFlow,
    signupFlow[currentStep].name,
    false,
  );
  // Faster velocity towards completion as we approach signup than after.
  let progress = 0.85 * ((totalStepsToEnd - stepsLeftToEnd) / totalStepsToEnd);
  if (stepsLeftToEnd === 0) {
    progress +=
      0.15 * ((totalStepsToVeryEnd - stepsLeftToVeryEnd) / totalStepsToVeryEnd);
  }
  const [, currentStepNode] = steps[currentStep];
  const whiteBackground = signupFlow[currentStep].whiteBackground;
  const countedSteps = signupFlow.filter((step) => !step.hideStepIndicator);
  const countedStepIndex = countedSteps.findIndex(
    (step) => step.name === signupFlow[currentStep].name,
  );
  const useStepDots = stepIndicator === "dots";

  return (
    <Container
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: whiteBackground && isMobile ? "white" : "#FDFAF7",
        "&.MuiContainer-root": {
          maxWidth: "none",
          padding: "0",
        },
      }}
    >
      <Box
        sx={{
          paddingTop: useStepDots ? "20px" : "8px",
          paddingBottom: useStepDots ? "32px" : 0,
          backgroundColor: useStepDots ? "transparent" : "white",
          ...(useStepDots ? progressBarSx : {}),
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            padding: isMobile ? "0 8px" : "0 16px",
            position: "relative",
          }}
        >
          <Collapse in={showBack} orientation="horizontal">
            <IconButton onClick={handleBack} disabled={!showBack}>
              <ArrowLeft />
            </IconButton>
          </Collapse>
          <FayeIcon
            style={{
              height: "26px",
              margin: "10px 0 8px",
            }}
          />
          {useStepDots && countedStepIndex >= 0 && (
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <StepDots
                activeIndex={countedStepIndex}
                totalSteps={countedSteps.length}
              />
            </Box>
          )}
        </Box>
        {!useStepDots && (
          <ProgressBar
            numerator={100 * progress}
            minimum={2.5}
            sx={progressBarSx}
          />
        )}
      </Box>
      <Box
        ref={stepRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          position: "relative",
          overflow: "scroll",
          scrollbarWidth: "none",
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
          padding: "0 16px",
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={{
              initial: { opacity: 0 },
              animate: {
                opacity: 1,
                transition: { duration: 0.2, ease: "easeIn" },
              },
              exit: {
                opacity: 0,
                transition: { duration: 0.2, ease: "easeOut" },
              },
            }}
            style={{
              width: "100%",
              height: "calc(100% - 64px)",
              padding: isMobile ? "" : "0 24px",
              display: "flex",
              justifyContent: "center",
              alignItems: "start",
            }}
          >
            {currentStepNode}
          </motion.div>
        </AnimatePresence>
      </Box>
    </Container>
  );
};
